import { useState, type MouseEvent } from 'react'
import { toast } from 'react-toastify'
import { downloadPdfBlob } from '../../utils/fetchPdfBlob'
import {
  canSharePdfFiles,
  downloadPdfFromUrl,
  printPdfFromUrl,
  sharePdfBlob,
  sharePdfFromUrl,
  type PrintPdfResult,
  type SharePdfResult,
} from '../../utils/pdfActions'
import { isDesktopBrowser } from '../../utils/pwa'
import {
  reportPdfAbsoluteUrl,
  type ReportPdfLink,
} from '../../utils/reportPdfLinks'
import styles from './ReportPdfMenu.module.css'

type BusyAction = 'download' | 'print' | 'share' | null
type ActionResult = void | SharePdfResult | PrintPdfResult

function finishToast(
  toastId: string | number | null,
  message: string,
  type: 'success' | 'info' | 'error' = 'success'
) {
  if (toastId == null) {
    if (type === 'error') toast.error(message)
    else if (type === 'info') toast.info(message, { autoClose: 4000 })
    else toast.success(message, { autoClose: 2500 })
    return
  }

  toast.update(toastId, {
    render: message,
    type,
    isLoading: false,
    autoClose: type === 'info' ? 4000 : 2500,
  })
}

async function runAction(
  action: Exclude<BusyAction, null>,
  fn: () => Promise<ActionResult>,
  setBusy: (value: BusyAction) => void
) {
  setBusy(action)
  const toastId = toast.loading('Загрузка документа…')
  try {
    const result = await fn()

    if (action === 'download' || result === 'downloaded') {
      finishToast(toastId, 'Скачано', 'success')
      return
    }

    if (action === 'print' && result === 'printed') {
      toast.dismiss(toastId)
      return
    }

    if (action === 'share' && result === 'aborted') {
      toast.dismiss(toastId)
      return
    }

    toast.dismiss(toastId)
  } catch {
    finishToast(toastId, 'Не удалось обработать документ', 'error')
  } finally {
    setBusy(null)
  }
}

export function PdfActionButtons({
  url,
  title,
}: {
  url: string
  title: string
}) {
  const [busy, setBusy] = useState<BusyAction>(null)
  const showPrint = isDesktopBrowser()
  // В попапе на ПК «Поделиться» не показываем — только в открытом просмотрщике
  const showShare = !isDesktopBrowser() && canSharePdfFiles()

  const onAction = (
    event: MouseEvent,
    action: Exclude<BusyAction, null>,
    fn: () => Promise<ActionResult>
  ) => {
    event.preventDefault()
    event.stopPropagation()
    if (busy) return
    void runAction(action, fn, setBusy)
  }

  return (
    <div className={styles.itemButtons}>
      <button
        type="button"
        className={styles.actionBtn}
        title="Скачать"
        aria-label="Скачать"
        disabled={!!busy}
        onClick={(e) =>
          onAction(e, 'download', () => downloadPdfFromUrl(url, title))
        }
      >
        <i className={`${styles.icon} ${styles.iconDownload}`} />
      </button>
      {showPrint && (
        <button
          type="button"
          className={styles.actionBtn}
          title="Напечатать"
          aria-label="Напечатать"
          disabled={!!busy}
          onClick={(e) =>
            onAction(e, 'print', () => printPdfFromUrl(url, title))
          }
        >
          <i className={`${styles.icon} ${styles.iconPrint}`} />
        </button>
      )}
      {showShare && (
        <button
          type="button"
          className={styles.actionBtn}
          title="Поделиться"
          aria-label="Поделиться"
          disabled={!!busy}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (busy) return
            void runAction(
              'share',
              () => sharePdfFromUrl(url, title),
              setBusy
            )
          }}
        >
          <i className={`${styles.icon} ${styles.iconShare}`} />
        </button>
      )}
    </div>
  )
}

export function ReportPdfMenuItems({ items }: { items: ReportPdfLink[] }) {
  return (
    <div className={styles.menu}>
      {items.map((item) => {
        const url = reportPdfAbsoluteUrl(item.path)
        return (
          <div key={`${item.title}-${item.path}`} className={styles.row}>
            <a
              target="_blank"
              href={url}
              className={styles.link}
              rel="noreferrer"
            >
              {item.title}
            </a>
            <PdfActionButtons url={url} title={item.title} />
          </div>
        )
      })}
    </div>
  )
}

export function PdfViewerActionButtons({
  blob,
  filename,
  title,
  disabled,
}: {
  blob: Blob | null
  filename: string
  title: string
  disabled?: boolean
}) {
  const [busy, setBusy] = useState<BusyAction>(null)
  const isDisabled = disabled || !blob || !!busy
  const showShare = canSharePdfFiles()

  return (
    <div className={styles.viewerActions}>
      <button
        type="button"
        className={styles.viewerIconBtn}
        title="Скачать"
        aria-label="Скачать"
        disabled={isDisabled}
        onClick={() => {
          if (isDisabled || !blob) return
          setBusy('download')
          try {
            downloadPdfBlob(blob, filename)
            toast.success('Скачано', { autoClose: 2500 })
          } catch {
            toast.error('Не удалось обработать документ')
          } finally {
            setBusy(null)
          }
        }}
      >
        <i className={`${styles.icon} ${styles.iconDownload}`} />
      </button>
      {showShare && (
        <button
          type="button"
          className={styles.viewerIconBtn}
          title="Поделиться"
          aria-label="Поделиться"
          disabled={isDisabled}
          onClick={() => {
            if (isDisabled || !blob) return
            const sharePromise = sharePdfBlob(blob, filename, title)
            setBusy('share')
            void sharePromise
              .catch(() => {
                toast.error('Не удалось обработать документ')
              })
              .finally(() => {
                setBusy(null)
              })
          }}
        >
          <i className={`${styles.icon} ${styles.iconShare}`} />
        </button>
      )}
    </div>
  )
}
